import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Catch Pose TV overlay.
 *
 * Renders the WebRTC MediaStream directly into a visible <video> element
 * (no canvas indirection). Drawing a 24–30fps video into a 1080p canvas
 * each tick was the main FPS bottleneck on Tizen/Android TV, so the canvas
 * path was removed: pixels go straight from the decoder to the compositor.
 */
export function CatchPoseTvOverlays() {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.autoplay = true;

    if (!stream) {
      if (video.srcObject) video.srcObject = null;
      return;
    }

    if (video.srcObject !== stream) {
      video.srcObject = stream;
    }

    let cancelled = false;
    let retryTimer: number | null = null;

    const tryPlay = (attempt: number): void => {
      if (cancelled) return;
      const p = video.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          console.warn(`[CatchPose] video.play() failed (attempt ${attempt})`, err);
          if (cancelled || attempt >= 5) return;
          retryTimer = window.setTimeout(() => tryPlay(attempt + 1), 400);
        });
      }
    };

    const onLoadedMeta = (): void => tryPlay(1);
    const onCanPlay = (): void => tryPlay(1);
    video.addEventListener('loadedmetadata', onLoadedMeta);
    video.addEventListener('canplay', onCanPlay);
    tryPlay(1);

    // Tag the track so Chromium's NetEQ-equivalent picks the realtime path.
    // Without this hint on some Android TV stacks the jitter buffer climbs into
    // seconds and never recovers.
    type HintedTrack = MediaStreamTrack & { contentHint?: string };
    for (const t of stream.getVideoTracks()) {
      try {
        (t as HintedTrack).contentHint = 'motion';
      } catch {
        /* ignore */
      }
    }

    // Live-edge keeper. On Android TV Chromium the WebRTC jitter buffer can
    // climb to multi-second values and stays there because jitterBufferTarget
    // API is not implemented. Two mitigations layered here:
    //   1. Seek to `buffered.end` if we are >150ms behind. For MediaStream
    //      srcObject this is usually a no-op (currentTime is read-only on
    //      many engines), but harmless when unsupported.
    //   2. Aggressive playbackRate ramp — the only knob that works reliably.
    //      Chromium honors playbackRate even on live MediaStream sources, so
    //      bursting to 1.5x for a moment drains the decoder backlog.
    //   3. If the drift exceeds 800ms, force a srcObject re-attach. This
    //      tears down the jitter buffer entirely. Costs ~200ms of black, but
    //      4s lag is worse.
    let lastReattachAt = 0;
    const liveEdgeIntervalId = window.setInterval(() => {
      if (cancelled || !video.srcObject) return;
      try {
        const ranges = video.buffered;
        if (ranges.length === 0) return;
        const liveEdge = ranges.end(ranges.length - 1);
        const behindSec = liveEdge - video.currentTime;
        const now = performance.now();

        if (behindSec > 0.8 && now - lastReattachAt > 5000) {
          lastReattachAt = now;
          console.warn(`[CatchPose] backlog ${behindSec.toFixed(2)}s — re-attaching srcObject`);
          const s = video.srcObject;
          video.srcObject = null;
          video.srcObject = s;
          video.play().catch(() => {});
          return;
        }
        if (behindSec > 0.15) {
          try { video.currentTime = liveEdge; } catch { /* read-only on some engines */ }
          video.playbackRate = 1.5;
        } else if (behindSec > 0.05) {
          video.playbackRate = 1.2;
        } else {
          video.playbackRate = 1;
        }
      } catch {
        /* ignore */
      }
    }, 300);

    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.clearInterval(liveEdgeIntervalId);
      video.removeEventListener('loadedmetadata', onLoadedMeta);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [stream]);

  const api = useMemo(
    () => ({
      attachStream(next: MediaStream | null) {
        setStream(next);
        setIsConnected(Boolean(next));
      },
      setConnected(next: boolean) {
        setIsConnected(next);
      },
      setVisible(next: boolean) {
        setIsVisible(next);
      },
      getVideoEl(): HTMLVideoElement | null {
        return videoRef.current;
      },
    }),
    [],
  );

  if (typeof window !== 'undefined') {
    (
      window as typeof window & {
        __catchPoseTvOverlayApi?: typeof api;
      }
    ).__catchPoseTvOverlayApi = api;
  }

  return (
    <div id="catch-pose-ui" className="catch-pose-ui" hidden={!isVisible}>
      <video
        ref={videoRef}
        id="catch-pose-video"
        className="catch-pose-ui__video"
        autoPlay
        playsInline
        muted
      />
      <div className="catch-pose-ui__badge">{isConnected ? 'CATCH POSE LIVE' : 'WAITING WEBRTC'}</div>
    </div>
  );
}
